import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Brain, Copy, Check, Clock, ChevronUp, ChevronDown, Sparkles } from "lucide-react";
import { useFontSize } from "@/hooks/use-font-size";
import React, { useState, useEffect, useRef, memo } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { useHapticFeedback } from "@/hooks/use-haptic";
import { Message, ModelType } from "@/types/chat";
import { AudioPlayerDialog } from "./audio-player-dialog";
import { detectLanguage, getFontFamilyForText, isArabicText } from "@/lib/language-detector";
import { useTranslation } from "@/lib/translation-utils";
import CSATFeedback from "@/components/feedback/csat-feedback";

// Optimized ThoughtProcessBubble with performance enhancements
const ThoughtProcessBubble = memo(function ThoughtProcessBubble({ 
  content, 
  isRTL, 
  fontSize, 
  expanded, 
  onToggle 
}: {
  content: string;
  isRTL: boolean;
  fontSize: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  // Use the memoized getFontFamily function
  const fontFamily = getFontFamily(isRTL ? "AI Reasoning" : "AI Reasoning");
  const contentFontFamily = getFontFamily(content);
  
  return (
    <motion.div
      className={cn(
        "rounded-xl message-bubble border border-primary/30 bg-background shadow-lg relative flex-1 mb-2 will-change-transform",
        fontSize < 14 ? "thought-bubble-padding-small" : "thought-bubble-padding-large",
        isRTL ? "text-right" : "text-left"
      )}
      style={{
        direction: isRTL ? 'rtl' : 'ltr',
        contain: 'content',
        willChange: 'transform'
      }}
      initial={false}
      layoutId={`thought-bubble-${expanded}`}
    >
      <button 
        onClick={onToggle}
        className="w-full flex items-center justify-between py-1.5 transition-colors outline-none cursor-pointer rounded-xl text-foreground/90"
        aria-expanded={expanded}
        aria-controls="thought-process-content"
      >
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-primary" />
          <span 
            className="font-semibold text-primary"
            style={{ 
              fontSize: `${fontSize}px`,
              fontFamily
            }}
          >
            AI Reasoning
          </span>
        </div>
        <motion.div 
          initial={false}
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="h-4 w-4 text-primary" />
        </motion.div>
      </button>

      <motion.div
        id="thought-process-content"
        initial={false}
        animate={{ 
          height: expanded ? 'auto' : 0,
          opacity: expanded ? 1 : 0
        }}
        transition={{ 
          duration: 0.2,
          ease: "easeInOut"
        }}
        className="overflow-hidden"
        style={{ 
          willChange: 'height, opacity',
          transform: 'translateZ(0)'
        }}
      >
        <div 
          className="text-xs sm:text-sm lg:text-base message-text whitespace-pre-wrap mt-4 ai-reasoning-text"
          style={{ 
            fontFamily: contentFontFamily,
            fontSize: `${fontSize}px`,
          }}
        >
          {content}
        </div>
      </motion.div>
    </motion.div>
  );
});

export function MessageBubble({ message, isLoading, isPartial, model, children }: MessageBubbleProps) {
  const isUser = message.role === "user";
  const { fontSize, screenSize } = useFontSize();
  const { triggerHaptic } = useHapticFeedback();
  const { t } = useTranslation(); // Add useTranslation hook to make component re-render on language change
  const [copied, setCopied] = useState(false);
  const [isRTL, setIsRTL] = useState(false);
  const [isAudioPlayerOpen, setIsAudioPlayerOpen] = useState(false);
  const [displayedContent, setDisplayedContent] = useState(message.content);
  const [thoughtExpanded, setThoughtExpanded] = useState(false);
  const messageBubbleRef = useRef<HTMLDivElement>(null);

  // Extract thought process from content
  const thoughtMatch = message.content.match(/<think>([\s\S]*?)<\/think>/);
  const thoughtContent = thoughtMatch ? thoughtMatch[1].trim() : null;
  const mainContent = message.content.replace(/<think>[\s\S]*?<\/think>/g, '').trim();

  useEffect(() => {
    setIsRTL(isArabicText(message.content));
  }, [message.content]);

  useEffect(() => {
    setDisplayedContent(mainContent);
  }, [mainContent]);

  const avatarSize = screenSize === 'desktop' ? 'w-12 h-12' : screenSize === 'tablet' ? 'w-10 h-10' : 'w-8 h-8';
  const padding = screenSize === 'desktop' ? 'px-6 py-4' : screenSize === 'tablet' ? 'px-5 py-3' : 'px-3 py-2';

  const formatTimestamp = (date: string | number | Date) => {
    return new Intl.DateTimeFormat('default', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(date));
  };

  const timestamp = formatTimestamp(message.timestamp);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      await triggerHaptic('success');
      setCopied(true);
      toast({ description: t("Message copied to clipboard"), duration: 2000 });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('[MessageBubble] Copy failed:', error);
      toast({
        description: t("Failed to copy message"),
        variant: "destructive",
        duration: 2000,
      });
    }
  };

  const handleMicClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('[MessageBubble] Summarize button clicked');
    try {
      await triggerHaptic("selection");
      setIsAudioPlayerOpen(true);
    } catch (error) {
      console.warn('[MessageBubble] Haptic failed:', error);
      setIsAudioPlayerOpen(true);
    }
    
    // Make sure audio dialog opens even if there are issues
    setTimeout(() => {
      if (!isAudioPlayerOpen) {
        console.log('[MessageBubble] Force opening audio player dialog');
        setIsAudioPlayerOpen(true);
      }
    }, 100);
  };

  // Optimized scroll handler with proper cleanup
  const handleScrollToMessage = async () => {
    try {
      // First trigger haptic feedback
      await triggerHaptic('selection');
      
      // Check if references exist before proceeding
      if (!messageBubbleRef.current) {
        console.warn('[MessageBubble] Cannot scroll - message ref is null');
        return;
      }
      
      const chatContainer = document.querySelector('.chat-main');
      if (!chatContainer) {
        console.warn('[MessageBubble] Cannot scroll - chat container not found');
        return;
      }
      
      // Calculate position outside of requestAnimationFrame for better performance
      // as DOM reads should be batched together
      const messagePosition = messageBubbleRef.current.offsetTop;
      
      // Use a tracked rAF ID so we can potentially cancel it
      let rafId: number;
      
      // Schedule the actual scrolling in the next frame
      rafId = requestAnimationFrame(() => {
        chatContainer.scrollTo({
          top: messagePosition - 100,
          behavior: 'smooth'
        });
      });
      
      // Return cleanup function that will be discarded in this case,
      // but follows the pattern for consistent code style
      return () => cancelAnimationFrame(rafId);
    } catch (error) {
      console.warn('[MessageBubble] Scroll failed:', error);
    }
  };

  // Detect if this is a system introduction or disclaimer message that shouldn't have controls
  const isSystemMessage = message.id === 'welcome' || message.id === 'disclaimer' || 
    message.content.includes("Welcome to") ||
    message.content.includes("Disclaimer:") ||
    message.content.includes("I'm Leen, your AI assistant") ||
    message.content.includes("Hello, I am Leen, an AI");

  const shouldShowControls = !isUser && (!isPartial || (model && model !== 'claude')) && !isLoading && !isSystemMessage;

  return (
    <div
      className={cn(
        "space-y-1 group hover:bg-muted/5 rounded-xl transition-colors duration-200 w-full",
        isUser ? "items-end" : "items-start"
      )}
      ref={messageBubbleRef}
    >
      <div className={cn(
        "flex items-start gap-2 sm:gap-3 lg:gap-4 relative w-full",
        isUser ? "flex-row-reverse" : "flex-row"
      )}>
        {!isUser && (
          <div className="flex flex-col items-center gap-2">
            <motion.div
              className={cn(avatarSize, "rounded-full overflow-hidden")}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              <img
                // Use properly optimized srcSet with explicit widths for better browser optimization
                srcSet={`${message.avatarSrc || "/assets/icons/Leen-Avatar.png"}`}
                // Precise size instructions help browser select the right image
                sizes="(max-width: 640px) 32px, (max-width: 768px) 40px, 48px"
                // Fallback image for browsers that don't support srcSet
                src={message.avatarSrc || "/assets/icons/Leen-Avatar.png"}
                alt="Leen Saudi Labor Law Expert"
                className="w-full h-full object-cover avatar-image"
                // Prioritize welcome message loading
                loading={message.id === 'welcome' ? 'eager' : 'lazy'}
                // Add decoding attribute to allow asynchronous decoding
                decoding="async"
                // Add width and height to prevent layout shifts (CLS)
                width={screenSize === 'desktop' ? 48 : screenSize === 'tablet' ? 40 : 32}
                height={screenSize === 'desktop' ? 48 : screenSize === 'tablet' ? 40 : 32}
              />
            </motion.div>
          </div>
        )}

        <div className="flex-1 flex flex-col">
          {!isUser && thoughtContent && (
            <ThoughtProcessBubble 
              content={thoughtContent}
              isRTL={isRTL}
              fontSize={fontSize}
              expanded={thoughtExpanded}
              onToggle={() => setThoughtExpanded(!thoughtExpanded)}
            />
          )}

          <motion.div
            className={cn(
              "rounded-xl message-bubble border border-primary/10",
              padding,
              isUser
                ? "bg-primary/10 text-foreground backdrop-blur-sm"
                : "text-foreground",
              isLoading && !message.content ? "loading-animation" : isPartial ? "typing-animation" : "",
              "relative flex-1"
            )}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              fontSize: `${fontSize}px`,
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            role="article"
            aria-label={`${isUser ? 'Your' : "Assistant's"} message: ${displayedContent ? displayedContent.substring(0, 50) + '...' : 'Loading...'}`}
          >
            {isLoading && !message.content ? (
              <div className="flex items-center justify-center min-h-[24px]">
                {children}
              </div>
            ) : (
              <>
                <div
                  className={cn(
                    "message-text space-y-2.5 sm:space-y-3",
                    isRTL ? "text-right" : "text-left"
                  )}
                  style={{
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }}
                >
                  {formatContent(displayedContent, fontSize)}
                </div>
                
                {/* Feedback option - only show for AI messages that aren't loading or partial */}
                {!isUser && !isLoading && !isPartial && !isSystemMessage && (
                  <div className="message-controls-row feedback-controls" style={{ justifyContent: "center" }}>
                    <CSATFeedback>
                      <button 
                        className="control-btn feedback-btn"
                        aria-label={t("Give Feedback")}
                      >
                        <span className="btn-text">✏️</span>
                        <span className="btn-text">{t("Give Feedback")}</span>
                      </button>
                    </CSATFeedback>
                  </div>
                )}

                {(shouldShowControls || isUser) && (
                  <div className="message-controls-row">
                    {/* Left side: Timestamp and copy button */}
                    <div className={cn(
                      "controls-left",
                      isRTL ? "rtl" : "ltr"
                    )}>
                      <div className="timestamp">
                        <Clock className="timestamp-icon" aria-hidden="true" />
                        <span className="timestamp-text">{timestamp}</span>
                      </div>

                      <div className="controls-divider" aria-hidden="true" />

                      <button
                        className="copy-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy();
                        }}
                        aria-label={`Copy ${isUser ? 'your' : "assistant's"} message`}
                      >
                        {copied ? (
                          <Check className="btn-icon" aria-hidden="true" />
                        ) : (
                          <Copy className="btn-icon" aria-hidden="true" />
                        )}
                      </button>
                    </div>

                    {/* Right side: Summarize and Read buttons */}
                    {shouldShowControls && (
                      <div className={cn(
                        "controls-right",
                        isRTL ? "rtl" : "ltr"
                      )}>
                        {isRTL ? (
                          // For RTL languages (Arabic), we swap the button order
                          <>
                            <button 
                              className="control-btn read-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleScrollToMessage();
                              }}
                              aria-label={t("Read message")}
                            >
                              <span className="btn-text">{t("Read")}</span>
                              <ChevronUp className="btn-icon" aria-hidden="true" />
                            </button>
                            
                            <button 
                              className="control-btn summarize-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMicClick(e);
                              }}
                              aria-label={t("Summarize message")}
                            >
                              <span className="summarize-emoji">📝</span>
                              <span className="btn-text">{t("Summarize")}</span>
                            </button>
                          </>
                        ) : (
                          // For LTR languages (English, etc.), keep original order
                          <>
                            <button 
                              className="control-btn summarize-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMicClick(e);
                              }}
                              aria-label={t("Summarize message")}
                            >
                              <span className="summarize-emoji">📝</span>
                              <span className="btn-text">{t("Summarize")}</span>
                            </button>
                            
                            <button 
                              className="control-btn read-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleScrollToMessage();
                              }}
                              aria-label={t("Read message")}
                            >
                              <span className="btn-text">{t("Read")}</span>
                              <ChevronUp className="btn-icon" aria-hidden="true" />
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </motion.div>
        </div>

        {isUser && (
          <div className="flex flex-col items-center gap-2">
            <div className={avatarSize} />
          </div>
        )}
      </div>
      <AudioPlayerDialog
        isOpen={isAudioPlayerOpen}
        onClose={() => setIsAudioPlayerOpen(false)}
        message={message}
      />
    </div>
  );
}

// Memoized getFontFamily function with a simple cache
const fontFamilyCache = new Map<string, string>();
const getFontFamily = (text: string | undefined): string => {
  // Return empty string for undefined or empty text
  if (!text) return '';
  
  // Use cached result if available
  if (fontFamilyCache.has(text)) {
    return fontFamilyCache.get(text)!;
  }
  
  // Calculate and cache result
  const result = getFontFamilyForText(text);
  fontFamilyCache.set(text, result);
  return result;
};

// Render inline content within a single block: split out inline **bold** spans
// while preserving the word-by-word font handling for mixed Arabic/English text.
function renderInline(text: string, keyBase: number | string): React.ReactNode {
  if (!text) return null;

  const segments = text.split(/(\*\*[^*]+\*\*)/g);
  return segments.map((segment, segIndex) => {
    if (!segment) return null;
    const key = `${keyBase}-${segIndex}`;

    // Inline bold (kept inline mid-sentence, e.g. "the **employer** must")
    if (segment.startsWith("**") && segment.endsWith("**") && segment.length > 4) {
      const inner = segment.slice(2, -2);
      return (
        <strong
          key={key}
          className="text-primary font-semibold"
          style={{ fontFamily: getFontFamily(inner) }}
        >
          {inner}
        </strong>
      );
    }

    // Mixed Arabic/English content needs per-word font selection
    if (segment.includes(" ") && /[\u0600-\u06FF]/.test(segment)) {
      const words = segment.split(/(\s+)/);
      return (
        <span key={key}>
          {words.map((word, wordIndex) => (
            <span key={`${key}-${wordIndex}`} style={{ fontFamily: getFontFamily(word) }}>
              {word}
            </span>
          ))}
        </span>
      );
    }

    return (
      <span key={key} style={{ fontFamily: getFontFamily(segment) }}>
        {segment}
      </span>
    );
  });
}

// Create a memoized version of the formatContent function
// Using React.useMemo pattern inside the component would be better,
// but this will still reduce repeated parsing for the same content
const contentCache = new Map<string, JSX.Element[]>();

function formatContent(content: string, fontSize: number): JSX.Element[] {
  // Create a cache key that includes both content and font size
  const cacheKey = `${content}_${fontSize}`;
  
  // Return cached result if available
  if (contentCache.has(cacheKey)) {
    return contentCache.get(cacheKey)!;
  }
  
  // Smart normalization that preserves structured content (code blocks, lists, etc.)
  const normalizedContent = (() => {
    // Step 1: Preserve code blocks and structured content by splitting and processing segments
    const codeBlockRegex = /```[\s\S]*?```/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    // Split content into code blocks and regular text
    while ((match = codeBlockRegex.exec(content)) !== null) {
      // Add text before the code block (normalized)
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: content.slice(lastIndex, match.index)
        });
      }
      // Add code block (preserved as-is)
      parts.push({
        type: 'code',
        content: match[0]
      });
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text after the last code block
    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.slice(lastIndex)
      });
    }
    
    // If no code blocks found, treat entire content as text
    if (parts.length === 0) {
      parts.push({
        type: 'text',
        content: content
      });
    }

    // Step 2: Apply normalization only to text segments
    return parts.map(part => {
      if (part.type === 'code') {
        return part.content; // Preserve code blocks unchanged
      } else {
        // Apply only light normalization here. Document structure (paragraphs,
        // lists, headers, section labels, quotes) is classified line-by-line in
        // the block parser below, so we must NOT collapse single newlines here.
        return part.content
          .replace(/[ \t]{2,}/g, ' ')   // collapse runs of spaces/tabs
          .replace(/[ \t]+\n/g, '\n')   // strip trailing spaces before newlines
          .replace(/\n{3,}/g, '\n\n');  // cap consecutive blank lines
      }
    }).join('');
  })();

  // Parse the normalized content into semantic blocks. Spacing BETWEEN blocks is
  // controlled entirely by CSS (the container's space-y-*), never by literal blank
  // lines, so responses stay tight and consistently organized.
  type Block =
    | { kind: "header"; level: number; content: string }
    | { kind: "label"; content: string }
    | { kind: "quote"; content: string }
    | { kind: "list"; items: string[] }
    | { kind: "code"; content: string }
    | { kind: "table"; header: string[]; rows: string[][] }
    | { kind: "paragraph"; content: string };

  const blocks: Block[] = [];
  const lines = normalizedContent.split("\n");

  // Matches a line that begins a list item: bullets, numbered, lettered, roman.
  const listLine = /^(?:[-*•]|\(?\d+[.)]|\(?[A-Za-z][.)]|\(?[ivxlcdm]+[.)]|\(?[IVXLCDM]+[.)])\s+/;

  // Markdown (GFM) table helpers. A table is a row line, followed by a separator
  // line of dashes/colons, followed by zero or more row lines. Rows start with "|".
  const isTableRow = (s: string) => s.startsWith("|") && s.indexOf("|", 1) !== -1;
  const isTableSeparator = (s: string) =>
    /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?$/.test(s);
  const parseTableRow = (s: string): string[] => {
    let cells = s.split("|");
    if (cells.length && cells[0].trim() === "") cells = cells.slice(1);
    if (cells.length && cells[cells.length - 1].trim() === "") cells = cells.slice(0, -1);
    return cells.map((c) => c.trim());
  };

  let paragraph: string[] = [];
  let listItems: string[] = [];
  let quoteLines: string[] = [];
  let inFence = false;
  let fenceLines: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ kind: "paragraph", content: paragraph.join(" ").trim() });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (listItems.length) {
      blocks.push({ kind: "list", items: listItems.slice() });
      listItems = [];
    }
  };
  const flushQuote = () => {
    if (quoteLines.length) {
      blocks.push({ kind: "quote", content: quoteLines.join(" ").trim() });
      quoteLines = [];
    }
  };
  const flushAll = () => {
    flushParagraph();
    flushList();
    flushQuote();
  };

  for (let li = 0; li < lines.length; li++) {
    const rawLine = lines[li];
    // Fenced code blocks are opaque: collect verbatim until the closing fence.
    if (/^```/.test(rawLine.trim())) {
      if (inFence) {
        blocks.push({ kind: "code", content: fenceLines.join("\n") });
        fenceLines = [];
        inFence = false;
      } else {
        flushAll();
        inFence = true;
      }
      continue;
    }
    if (inFence) {
      fenceLines.push(rawLine);
      continue;
    }

    const line = rawLine.trim();

    // Blank line: a separator only (its spacing comes from CSS, not the newline).
    if (!line) {
      flushAll();
      continue;
    }

    // Headers (#, ##, ...)
    const header = line.match(/^(#{1,6})\s+(.*)$/);
    if (header) {
      flushAll();
      blocks.push({ kind: "header", level: header[1].length, content: header[2].trim() });
      continue;
    }

    // A whole line that is only **...** (optional trailing colon) is a section label.
    const label = line.match(/^\*\*([^*\n]+)\*\*\s*:?\s*$/);
    if (label) {
      flushAll();
      blocks.push({ kind: "label", content: label[1].trim() });
      continue;
    }

    // Markdown tables: a row line immediately followed by a separator line.
    if (
      isTableRow(line) &&
      li + 1 < lines.length &&
      isTableSeparator(lines[li + 1].trim())
    ) {
      flushAll();
      const header = parseTableRow(line);
      li++; // consume the separator line
      const rows: string[][] = [];
      while (li + 1 < lines.length && isTableRow(lines[li + 1].trim())) {
        li++;
        rows.push(parseTableRow(lines[li].trim()));
      }
      blocks.push({ kind: "table", header, rows });
      continue;
    }

    // Blockquotes (group consecutive > lines)
    if (/^>\s?/.test(line)) {
      flushParagraph();
      flushList();
      quoteLines.push(line.replace(/^>\s?/, ""));
      continue;
    }

    // List items (group consecutive into one list block)
    if (listLine.test(line)) {
      flushParagraph();
      flushQuote();
      listItems.push(line);
      continue;
    }

    // Prose: consecutive prose lines join into a single paragraph (soft-wrap repair)
    flushList();
    flushQuote();
    paragraph.push(line);
  }
  if (inFence && fenceLines.length) {
    blocks.push({ kind: "code", content: fenceLines.join("\n") });
  }
  flushAll();

  const result = blocks.map((block, index): JSX.Element => {
    switch (block.kind) {
      case "header": {
        // Level-1 header (e.g. the "Quick Answer" lead) is a prominent callout bar
        if (block.level === 1) {
          return (
            <div
              key={index}
              className="flex items-center gap-2 font-bold text-primary px-3 py-2 rounded-xl bg-primary/10 border border-primary/20 text-base sm:text-lg"
              style={{ fontFamily: getFontFamily(block.content) }}
            >
              <Sparkles className="h-5 w-5 shrink-0" />
              <span>{block.content}</span>
            </div>
          );
        }
        // Lower-level headers are clean section titles
        return (
          <div
            key={index}
            className="font-semibold text-primary text-sm sm:text-base"
            style={{ fontFamily: getFontFamily(block.content) }}
          >
            {block.content}
          </div>
        );
      }
      case "label":
        return (
          <div
            key={index}
            className="font-semibold text-primary text-sm sm:text-base"
            style={{ fontFamily: getFontFamily(block.content) }}
          >
            {block.content}
          </div>
        );
      case "quote":
        return (
          <blockquote
            key={index}
            className="px-4 py-2.5 border-s-4 border-primary/40 bg-primary/10 rounded-xl text-foreground/90 leading-relaxed"
            style={{ fontFamily: getFontFamily(block.content), fontSize: `${fontSize}px` }}
          >
            {renderInline(block.content, index)}
          </blockquote>
        );
      case "list":
        return (
          <div key={index} className="space-y-1">
            {block.items.map((item, itemIndex) => (
              <div key={`${index}-${itemIndex}`} className="leading-relaxed">
                {renderInline(item, `${index}-${itemIndex}`)}
              </div>
            ))}
          </div>
        );
      case "code":
        return (
          <pre
            key={index}
            className="bg-muted/50 rounded-lg p-3 overflow-x-auto text-sm whitespace-pre"
          >
            <code>{block.content}</code>
          </pre>
        );
      case "table":
        return (
          <div
            key={index}
            className="overflow-x-auto rounded-xl border border-primary/20"
          >
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-primary/10">
                  {block.header.map((cell, cellIndex) => (
                    <th
                      key={`${index}-h-${cellIndex}`}
                      className="px-3 py-2 text-start font-semibold text-primary border-b border-primary/20 align-top"
                      style={{ fontFamily: getFontFamily(cell) }}
                    >
                      {renderInline(cell, `${index}-h-${cellIndex}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, rowIndex) => (
                  <tr
                    key={`${index}-r-${rowIndex}`}
                    className="border-b border-border/40 last:border-0"
                  >
                    {row.map((cell, cellIndex) => (
                      <td
                        key={`${index}-${rowIndex}-${cellIndex}`}
                        className="px-3 py-2 align-top leading-relaxed"
                        style={{ fontFamily: getFontFamily(cell) }}
                      >
                        {renderInline(cell, `${index}-${rowIndex}-${cellIndex}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case "paragraph":
      default:
        return (
          <p key={index} className="leading-relaxed" style={{ fontFamily: getFontFamily(block.content) }}>
            {renderInline(block.content, index)}
          </p>
        );
    }
  });
  
  // Store the result in the cache before returning
  contentCache.set(cacheKey, result);
  
  // Limit cache size to prevent memory issues (keep last 100 entries)
  if (contentCache.size > 100) {
    // Safely get the first key, with type checking
    const keys = Array.from(contentCache.keys());
    if (keys.length > 0) {
      contentCache.delete(keys[0]);
    }
  }
  
  return result;
}

type MessageBubbleProps = {
  message: Message;
  isLoading?: boolean;
  isPartial?: boolean;
  model?: ModelType;
  children?: React.ReactNode;
};